import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { SBS_ROLES, STUDENT_ROLES } from '../../permissions/roles.js';

const STUDENT_ROLE_NAMES: string[] = STUDENT_ROLES;
const SB_ROLE_NAMES: string[] = SBS_ROLES;

/**
 * A relative as the relations card may show them. Deliberately not a `Person`: family members read each other only
 * through this shape, so nothing beyond it - address, bank data, mandates - can be selected.
 */
@Entity()
export class FamilyRelative extends AdornisEntity {
  static override _class = 'FamilyRelative';

  @Field(() => ID)
  _id!: string;

  @Field(() => String)
  vorname: Maybe<string>;

  @Field(() => String)
  nachname: Maybe<string>;

  /** internal viewers only - it is also the login name */
  @Field(() => String)
  debitorennummer: Maybe<string>;

  /** role names only - their contexts would tell schools and children apart */
  @Field(() => [String], { default: v => v ?? [] })
  roles: Maybe<string[]>;

  /** the parent's side of every relation, limited to the relatives handed out with it */
  @Field(() => [String], { default: v => v ?? [] })
  sorgeberechtigteKinderIDs: Maybe<string[]>;

  @Field(() => [String], { default: v => v ?? [] })
  spendenKinderIDs: Maybe<string[]>;

  /** set for students - how old a sibling is matters - and for adults only for internal viewers */
  @Field(() => Date)
  geburtsDatum: Maybe<Date>;

  /** grade and class identifier of the active class booking, e.g. "10a" */
  @Field(() => String)
  klasse: Maybe<string>;

  /** the groups of the active class booking - for a former student the school of that role */
  @Field(() => String)
  schule: Maybe<string>;

  /** internal viewers only, like the email */
  @Field(() => String)
  telefonNr: Maybe<string>;

  @Field(() => String)
  email: Maybe<string>;

  @Field(() => Boolean, { default: v => v ?? false })
  canOpenProfile!: boolean;
}

/** a parent who went to a Martinus school keeps the old student role - the parent role wins */
export const isStudentRelative = (relative: Pick<FamilyRelative, 'roles'>) =>
  (relative.roles ?? []).some(role => STUDENT_ROLE_NAMES.includes(role)) &&
  !(relative.roles ?? []).some(role => SB_ROLE_NAMES.includes(role));
